import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';

/* Below 640px the table collapses: header hidden, each cell stacked with its micro label. */
const ROW = 'block cursor-pointer py-2 sm:table-row sm:py-0';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const fetchClients = async (q) => {
    try {
      const res = await getClients(q || undefined);
      setClients(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchClients(); }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    fetchClients(e.target.value);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error('Client name is required'); return; }
    if (form.email.trim() && !isValidEmail(form.email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setSaving(true);
    try {
      await createClient(form);
      toast.success('Client added');
      setShowAdd(false);
      setForm({ name: '', email: '', company: '', phone: '', address: '', notes: '' });
      fetchClients(search);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to add client';
      toast.error(msg);
    }
    setSaving(false);
  };

  const open = (c) => navigate(`/clients/${c.id}`);

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clients"
        description={`${clients.length} client${clients.length !== 1 ? 's' : ''}`}
        actions={
          <Button onClick={() => setShowAdd(true)} data-testid="add-client-btn">
            <Plus strokeWidth={1.5} /> Add client
          </Button>
        }
      />

      <div className="relative">
        <Search strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
        <Input
          placeholder="Search clients"
          value={search}
          onChange={handleSearch}
          className="pl-9"
          data-testid="client-search"
        />
      </div>

      {loading ? (
        <div className="mt-6 space-y-px border-t border-line pt-px">
          {[1, 2, 3].map(i => <div key={i} className="h-11 animate-pulse bg-wash" />)}
        </div>
      ) : clients.length === 0 ? (
        <EmptyState
          className="mt-6"
          title="No clients yet"
          description="Add your first client to start sending proposals and invoices."
          action={<Button onClick={() => setShowAdd(true)}>Add client</Button>}
        />
      ) : (
        <div className="mt-6">
          <Table>
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow
                  key={c.id}
                  className={ROW}
                  role="link"
                  tabIndex={0}
                  onClick={() => open(c)}
                  onKeyDown={e => { if (e.key === 'Enter') open(c); }}
                  data-testid={`client-card-${c.id}`}
                >
                  <TableCell data-label="Client" className={`${CELL} text-ink`}>{c.name}</TableCell>
                  <TableCell data-label="Company" className={`${CELL} text-ink-muted`}>{c.company || '—'}</TableCell>
                  <TableCell data-label="Email" className={`${CELL} text-ink-muted`}>{c.email || '—'}</TableCell>
                  <TableCell data-label="Phone" className={`${CELL} text-ink-muted`}>{c.phone || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md" data-testid="add-client-dialog">
          <DialogHeader>
            <DialogTitle>Add new client</DialogTitle>
            <DialogDescription>Enter your client's details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Name" htmlFor="client-name" hint="Required">
              <Input id="client-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Client name" data-testid="client-name-input" />
            </Field>
            <Field label="Email" htmlFor="client-email" error={emailError}>
              <Input
                id="client-email"
                value={form.email}
                onChange={e => { setForm({ ...form, email: e.target.value }); setEmailError(''); }}
                placeholder="client@company.com"
                className={emailError ? 'border-destructive focus-visible:ring-destructive' : ''}
                data-testid="client-email-input"
              />
            </Field>
            <Field label="Company" htmlFor="client-company">
              <Input id="client-company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Company name" data-testid="client-company-input" />
            </Field>
            <Field label="Phone" htmlFor="client-phone">
              <Input id="client-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 000-0000" data-testid="client-phone-input" />
            </Field>
            <Field label="Address" htmlFor="client-address">
              <Textarea id="client-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder={'123 Main St, Suite 4\nAustin, TX 78701\nUSA'} data-testid="client-address-input" />
            </Field>
            <Field label="Notes" htmlFor="client-notes">
              <Textarea id="client-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes" data-testid="client-notes-input" />
            </Field>
            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleAdd} disabled={saving} data-testid="save-client-btn">
                {saving ? 'Saving…' : 'Add client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
